import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { toast } from "sonner";
import { safeForeground } from "../../lib/branding";

export default function Branding() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null),
    [error, setError] = useState(""),
    [saving, setSaving] = useState(false);
  useEffect(() => {
    api
      .get("/config/branding")
      .then(({ data }) =>
        setForm({
          platform_name: data.platform_name || "",
          platform_tagline: data.platform_tagline || "",
          brand_primary: data.brand_primary || "#0A0A0A",
          brand_secondary: data.brand_secondary || "#334155",
          brand_accent: data.brand_accent || "#4F46E5",
          brand_text: data.brand_text || "#FFFFFF",
          logo_path: data.logo_path || null,
        }),
      )
      .catch((e) =>
        setError(formatApiError(e.response?.data?.detail || e.message)),
      );
  }, []);
  if (!form) return <p>Loading organisation identity…</p>;
  const foreground = safeForeground(form.brand_accent);
  const uploadLogo = async (file) => {
    if (!file) return;
    const body = new FormData(); body.append("file", file);
    try { const { data } = await api.post("/config/branding/logo", body); setForm({ ...form, logo_path: data.logo_path }); await refresh(); toast.success("Organisation logo updated"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail || err.message)); }
  };
  const removeLogo = async () => { await api.delete("/config/branding/logo"); setForm({ ...form, logo_path: null }); await refresh(); toast.success("Organisation logo removed"); };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.patch("/config/branding", form);
      await refresh();
      toast.success("Organisation branding updated");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[.2em] text-indigo-600">
          Configlution / Organisation
        </p>
        <h1 className="mt-2 text-3xl font-black">Branding</h1>
        <p className="mt-2 text-sm text-slate-600">
          Make the workspace recognisably yours while retaining the Webstax
          platform identity.
        </p>
      </header>
      <form
        onSubmit={save}
        className="grid overflow-hidden rounded-2xl border bg-white shadow-sm md:grid-cols-2"
      >
        <div className="space-y-4 p-6">
          <Field
            label="Organisation display name"
            name="platform_name"
            form={form}
            setForm={setForm}
          />
          <Field
            label="Tagline"
            name="platform_tagline"
            form={form}
            setForm={setForm}
          />
          <div className="grid grid-cols-2 gap-4">
            {[
              "brand_primary",
              "brand_secondary",
              "brand_accent",
              "brand_text",
            ].map((name) => (
              <Field
                key={name}
                label={
                  name
                    .replace("brand_", "")
                    .replace(/^./, (x) => x.toUpperCase()) + " colour"
                }
                name={name}
                type="color"
                form={form}
                setForm={setForm}
              />
            ))}
          </div>
          <div className="rounded-xl border p-4">
            <label className="block text-sm font-semibold">Organisation logo<input aria-label="Choose organisation logo" className="mt-2 block w-full text-xs" type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif" onChange={(e) => uploadLogo(e.target.files?.[0])}/></label>
            {form.logo_path && <div className="mt-3 flex items-center gap-3"><img className="h-14 max-w-40 object-contain" alt="Organisation logo preview" src={`/api/files/${form.logo_path}`}/><button type="button" onClick={removeLogo} className="text-sm font-semibold text-red-700">Remove logo</button></div>}
          </div>
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <button
            disabled={saving}
            className="rounded-lg bg-slate-950 px-5 py-2.5 font-semibold text-white"
          >
            {saving ? "Saving…" : "Save branding"}
          </button>
        </div>
        <div
          className="flex min-h-64 items-center justify-center p-8"
          style={{
            background: `linear-gradient(135deg,${form.brand_primary},${form.brand_secondary})`,
            color: form.brand_text,
          }}
        >
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-[.25em] opacity-70">
              Powered by Webstax
            </div>
            {form.logo_path && <img className="mx-auto mt-4 h-16 max-w-52 object-contain" alt="" src={`/api/files/${form.logo_path}`}/>}
            <div className="mt-3 text-3xl font-black">
              {form.platform_name || "Your organisation"}
            </div>
            <div className="mt-2 opacity-80">{form.platform_tagline}</div>
            <button
              type="button"
              className="mt-5 rounded-lg px-4 py-2 font-bold"
              style={{ background: form.brand_accent, color: foreground }}
            >
              Example action
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
function Field({ label, name, form, setForm, type = "text" }) {
  return (
    <label className="block text-sm font-medium text-slate-700">
      {label}
      <input
        aria-label={label}
        type={type}
        value={form[name]}
        onChange={(e) =>
          setForm((current) => ({ ...current, [name]: e.target.value }))
        }
        className={`mt-1.5 block rounded-lg border border-slate-300 ${type === "color" ? "h-11 w-full p-1" : "w-full px-3 py-2.5"}`}
      />
    </label>
  );
}
